// #772 part 2 -- the drawing's grid (docs/model/drawing-ui-viewport.md): drawn in drawing units
// from the page origin, so it pans and zooms with the drawing, as lines or dots, with every fifth
// line (or dot) stronger. Hidden by default. Drawn on a canvas in screen space, so panning and
// zooming cost one redraw and nothing in React.
export type GridKind = 'line' | 'dot';

export interface GridView {
  readonly kind: GridKind;
  // Spacing in drawing units (1 unit = 1 CSS px at 100%).
  readonly spacing: number;
  readonly zoom: number;
  readonly panX: number;
  readonly panY: number;
}

export interface GridRenderer {
  resize(width: number, height: number): void;
  // null hides the grid.
  update(view: GridView | null): void;
  dispose(): void;
}

export const MAJOR_EVERY = 5;
// Zoomed far out, grid lines closer than this on screen are skipped (every fifth is kept, then
// every twenty-fifth, ...), so the grid thins out instead of turning into a solid tint.
export const MIN_SCREEN_STEP = 8;

// The on-screen step and how many grid steps it spans, after thinning.
export function visibleStep(spacing: number, zoom: number): { readonly screenStep: number; readonly every: number } {
  let every = 1;
  while (spacing * zoom * every < MIN_SCREEN_STEP && every < 1e6) every *= MAJOR_EVERY;
  return { screenStep: spacing * zoom * every, every };
}

// Grid indices (in steps of `every`) whose lines fall inside [0, extent] on screen.
export function visibleIndices(pan: number, screenStep: number, extent: number): { first: number; last: number } {
  return { first: Math.ceil(-pan / screenStep), last: Math.floor((extent - pan) / screenStep) };
}

function readCssColor(element: HTMLElement, variableName: string, fallback: string): string {
  return getComputedStyle(element).getPropertyValue(variableName).trim() || fallback;
}

export function createGridRenderer(canvas: HTMLCanvasElement): GridRenderer {
  const context = canvas.getContext('2d');
  let cssWidth = 1;
  let cssHeight = 1;
  let current: GridView | null = null;

  const draw = (): void => {
    if (!context) return;
    const dpr = Math.max(1, globalThis.devicePixelRatio || 1);
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, cssWidth, cssHeight);
    const view = current;
    if (!view || !(view.spacing > 0) || !(view.zoom > 0)) return;

    const owner = canvas.parentElement ?? canvas;
    const minor = readCssColor(owner, '--grid-minor', 'rgba(80, 100, 120, 0.10)');
    const major = readCssColor(owner, '--grid-major', 'rgba(80, 100, 120, 0.20)');
    const { screenStep, every } = visibleStep(view.spacing, view.zoom);
    const isMajor = (index: number) => (index * every) % MAJOR_EVERY === 0;
    const xs = visibleIndices(view.panX, screenStep, cssWidth);
    const ys = visibleIndices(view.panY, screenStep, cssHeight);

    if (view.kind === 'dot') {
      context.fillStyle = major;
      for (let i = xs.first; i <= xs.last; i += 1) {
        const x = view.panX + i * screenStep;
        for (let j = ys.first; j <= ys.last; j += 1) {
          const y = view.panY + j * screenStep;
          // A dot covers far less than a line, so every dot takes the stronger colour (the faint
          // one vanishes on dark themes); major dots are larger instead.
          const size = isMajor(i) && isMajor(j) ? 3 : 1.75;
          context.fillRect(Math.round(x) - size / 2, Math.round(y) - size / 2, size, size);
        }
      }
      return;
    }

    // Minor lines in one path, major lines in another: two strokes however many lines.
    for (const strong of [false, true]) {
      context.beginPath();
      for (let i = xs.first; i <= xs.last; i += 1) {
        if (isMajor(i) !== strong) continue;
        const x = Math.round(view.panX + i * screenStep) + 0.5;
        context.moveTo(x, 0);
        context.lineTo(x, cssHeight);
      }
      for (let j = ys.first; j <= ys.last; j += 1) {
        if (isMajor(j) !== strong) continue;
        const y = Math.round(view.panY + j * screenStep) + 0.5;
        context.moveTo(0, y);
        context.lineTo(cssWidth, y);
      }
      context.strokeStyle = strong ? major : minor;
      context.lineWidth = strong ? 1 : 0.5;
      context.stroke();
    }
  };

  return {
    resize(width: number, height: number): void {
      cssWidth = Math.max(1, width);
      cssHeight = Math.max(1, height);
      const dpr = Math.max(1, globalThis.devicePixelRatio || 1);
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      draw();
    },
    update(view: GridView | null): void {
      current = view;
      draw();
    },
    dispose(): void {
      current = null;
      if (context) {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };
}
